/**
 * OneSignal Web Push Notification helpers.
 * The native Median wrapper handles push natively —
 * this module covers web-only users.
 */

declare global {
  interface Window {
    OneSignalDeferred?: Array<(OneSignal: any) => void | Promise<void>>;
  }
}

export function initOneSignal() {
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(async function (OneSignal: any) {
    await OneSignal.init({
      appId: "6c803af8-9776-4e05-9983-8f6ad5ba80fc",
      notifyButton: { enable: false },
      allowLocalhostAsSecureOrigin: true,
      welcomeNotification: {
        title: "HydroLife 💧",
        message: "Hydration reminders are now active!",
      },
    });
  });
}

export function requestNotificationPermission() {
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(function (OneSignal: any) {
    OneSignal.Slidedown.promptPush();
  });
}

export function sendLocalNotification(title: string, body: string) {
  if ("Notification" in window && Notification.permission === "granted") {
    new Notification(title, { body, icon: "/pwa-192x192.png" });
  }
}

// --- Contextual message helpers ---

function getTimeBasedMessages(): string[] {
  const hour = new Date().getHours();
  if (hour >= 6 && hour < 12) {
    return [
      "🌅 Start your day with water!",
      "🌅 Morning hydration boosts your energy!",
      "🌅 A glass of water to kickstart the day!",
    ];
  }
  if (hour >= 12 && hour < 18) {
    return [
      "☀️ Midday hydration check",
      "☀️ Afternoon pick-me-up — drink some water!",
      "☀️ Keep the momentum going with a glass!",
    ];
  }
  if (hour >= 18 && hour < 22) {
    return [
      "🌙 Evening refresh",
      "🌙 Wind down with a glass of water",
      "🌙 Evening hydration for a better sleep!",
    ];
  }
  return [];
}

function getContextualMessage(streak: number, glasses: number, goal: number): string {
  const hour = new Date().getHours();

  // Sleep hours — shouldn't reach here, but safety
  if (hour >= 22 || hour < 6) return "";

  // Goal reached
  if (glasses >= goal) return "🎯 Goal reached! Great job!";

  // Streak context
  if (streak >= 7) return `🔥 ${streak} day streak! Keep going!`;

  // Behind pace
  const remaining = goal - glasses;
  if (glasses > 0 && remaining > 0) {
    return `💪 You've got this! ${remaining} glass${remaining !== 1 ? "es" : ""} to go`;
  }

  // First drink
  if (glasses === 0) return "👋 First drink of the day!";

  // Time-based fallback
  const timeMessages = getTimeBasedMessages();
  if (timeMessages.length > 0) {
    return timeMessages[Math.floor(Math.random() * timeMessages.length)];
  }

  return "Time to drink water! 💧";
}

// --- Reminder state ---

let reminderInterval: ReturnType<typeof setInterval> | null = null;
let currentStreak = 0;
let currentGlasses = 0;
let currentGoal = 8;
let isHotWeather = false;

const NORMAL_INTERVAL_MS = 90 * 60 * 1000; // 90 minutes
const HOT_INTERVAL_MS = 60 * 60 * 1000; // 60 minutes when > 30°C

export function updateReminderContext(ctx: {
  streak?: number;
  glasses?: number;
  goal?: number;
  isHot?: boolean;
}) {
  if (ctx.streak !== undefined) currentStreak = ctx.streak;
  if (ctx.glasses !== undefined) currentGlasses = ctx.glasses;
  if (ctx.goal !== undefined) currentGoal = ctx.goal;
  if (ctx.isHot !== undefined) {
    const wasHot = isHotWeather;
    isHotWeather = ctx.isHot;
    // Restart interval if weather changes frequency
    if (reminderInterval && wasHot !== isHotWeather) {
      stopHydrationReminders();
      startHydrationReminders();
    }
  }
}

function isQuietHours(): boolean {
  const hour = new Date().getHours();
  return hour >= 22 || hour < 6;
}

export function startHydrationReminders() {
  if (reminderInterval) return () => clearInterval(reminderInterval!);

  const intervalMs = isHotWeather ? HOT_INTERVAL_MS : NORMAL_INTERVAL_MS;

  reminderInterval = setInterval(() => {
    // Don't send during quiet hours
    if (isQuietHours()) return;

    // Don't notify if goal already reached
    if (currentGlasses >= currentGoal) return;

    // Hot weather message takes priority
    let message: string;
    if (isHotWeather) {
      message = "☀️ It's hot! Stay hydrated 💧";
    } else {
      message = getContextualMessage(currentStreak, currentGlasses, currentGoal);
    }

    if (message) {
      sendLocalNotification("HydroLife 💧", message);
    }
  }, intervalMs);

  return () => {
    stopHydrationReminders();
  };
}

export function stopHydrationReminders() {
  if (reminderInterval) {
    clearInterval(reminderInterval);
    reminderInterval = null;
  }
}
